/**
 * TruePrice V1 - Core Calculation Engine
 *
 * Pure functions for all pricing calculations.
 * Source: SSOT Core Formulas, Addendums D & E
 */

/**
 * Bump this manually whenever calibration parameters change:
 * PSI weights, tier multipliers, size curve breakpoints, medium multipliers.
 */
export const ENGINE_VERSION = '1.0.0';

import {
  MediumKey,
  TierKey,
  CareerStageKey,
  TIERS,
  CAREER_STAGES,
  MEDIUMS,
  VerdictInputs,
  VerdictCalculations,
  VerdictFlags,
  Verdict,
  getSustainabilityStatus,
  findTierByDensity,
} from '../types';

import {
  PSI_BASE,
  MASTER_PSI,
  interpolateSizeMultiplier,
  getDetailPremium,
  SIZE_THRESHOLDS,
  DENSITY_RELAXER_FACTOR,
  MATERIAL_EXCEPTION_THRESHOLD,
  MINIMUM_FLOOR_MULTIPLIER,
  getSubstrateModifier,
} from '../constants';

// ============================================================================
// FLOOR CALCULATIONS (TTCP)
// ============================================================================

/**
 * Calculate Survival Target (hourly break-even rate)
 * ST = B / C
 * 
 * @param monthlyBurn - Monthly expenses in cents
 * @param monthlyCapacity - Monthly billable hours
 * @returns Cents per hour
 */
export function calculateSurvivalTarget(
  monthlyBurn: number,
  monthlyCapacity: number
): number {
  if (monthlyBurn <= 0 || monthlyCapacity <= 0) {
    return 0; // Zero ST → floor = materials only (economically valid)
  }
  return monthlyBurn / monthlyCapacity;
}

/**
 * Calculate Labor Value
 * LaborValue = H × ST × F
 * 
 * @param hours - Hours worked
 * @param survivalTarget - ST in cents per hour
 * @param fidelityMultiplier - F from tier lookup
 * @returns Labor value in cents
 */
export function calculateLaborValue(
  hours: number,
  survivalTarget: number,
  fidelityMultiplier: number
): number {
  return hours * survivalTarget * fidelityMultiplier;
}

/**
 * Calculate TTCP (Total True Cost Price / Floor)
 * TTCP = (LaborValue + M) × (1 + T)
 * 
 * @param laborValue - Calculated labor value in cents
 * @param materials - Material costs in cents
 * @param scopeTax - T from tier lookup (decimal, e.g., 0.08)
 * @returns TTCP in cents (NOT rounded - per SSOT binding clarification)
 */
export function calculateTTCP(
  laborValue: number,
  materials: number,
  scopeTax: number
): number {
  const baseCost = laborValue + materials;
  return baseCost * (1 + scopeTax);
}

/**
 * Complete floor calculation
 */
export function calculateFloor(
  hours: number,
  monthlyBurn: number,
  monthlyCapacity: number,
  materials: number,
  tier: TierKey
): { ttcp: number; survivalTarget: number; laborValue: number; baseCost: number; scopeTax: number } {
  const tierConfig = TIERS[tier];
  const survivalTarget = calculateSurvivalTarget(monthlyBurn, monthlyCapacity);
  const laborValue = calculateLaborValue(hours, survivalTarget, tierConfig.multiplier);
  const baseCost = laborValue + materials;
  const scopeTaxAmount = baseCost * tierConfig.scopeTax;
  const ttcp = Math.max(baseCost + scopeTaxAmount, materials); // Floor can never be below material cost

  return {
    ttcp,
    survivalTarget,
    laborValue,
    baseCost,
    scopeTax: scopeTaxAmount,
  };
}

// ============================================================================
// CEILING CALCULATIONS (P_max)
// ============================================================================

/**
 * Get effective PSI base considering master boost
 * Source: Addendum E Section 3.5
 */
export function getEffectivePSI(
  medium: MediumKey,
  careerStage: CareerStageKey,
  empiricalPSICents?: number | null
): { psiBase: number; masterBoostApplied: boolean } {
  // If empirical data exists (from user_sales aggregation), use it directly.
  // No Master PSI boost on top — empirical data already includes master-level sales.
  // No sell-through discount — these are real transactions, not ask prices.
  if (empiricalPSICents != null && empiricalPSICents > 0) {
    return {
      psiBase: empiricalPSICents / 100, // cents → dollars
      masterBoostApplied: false,
    };
  }

  // Fallback: hardcoded constants
  const isEstablished = careerStage === 'established';

  if (isEstablished && MASTER_PSI[medium] > PSI_BASE[medium]) {
    return {
      psiBase: MASTER_PSI[medium],
      masterBoostApplied: true,
    };
  }

  return {
    psiBase: PSI_BASE[medium],
    masterBoostApplied: false,
  };
}

/**
 * Calculate market ceiling (P_max)
 * P_max = PSI_effective × Area × S_size × C_stage × substrateModifier
 *
 * Source: Addendum D Section 3.1, Addendum E Section 4.1, P12 (substrate)
 */
export function calculateCeiling(
  medium: MediumKey,
  area: number,
  careerStage: CareerStageKey,
  tier: TierKey,
  substrate?: string
): {
  ceiling: number;
  psiBase: number;
  psiEffective: number;
  sizeMultiplier: number;
  detailPremium: number;
  substrateModifier: number;
  masterBoostApplied: boolean;
  detailPremiumApplied: boolean;
} {
  // Get PSI with master boost if applicable
  const { psiBase, masterBoostApplied } = getEffectivePSI(medium, careerStage);

  // Get detail premium for L4+ small works
  const isHighTier = tier === 'L4' || tier === 'L5';
  const detailPremium = getDetailPremium(area, isHighTier);
  const detailPremiumApplied = detailPremium > 1.0;

  // Calculate effective PSI
  const psiEffective = psiBase * detailPremium;

  // Get size multiplier from decay curve
  const sizeMultiplier = interpolateSizeMultiplier(area);

  // Get career stage multiplier
  const stageMultiplier = CAREER_STAGES[careerStage].multiplier;

  // Get substrate modifier (ceiling only — market perception, not cost)
  const subMod = getSubstrateModifier(medium, substrate);

  // Calculate ceiling (in cents)
  // PSI is in dollars, so multiply by 100 to convert to cents
  const ceiling = psiEffective * 100 * area * sizeMultiplier * stageMultiplier * subMod;

  return {
    ceiling,
    psiBase,
    psiEffective,
    sizeMultiplier,
    detailPremium,
    substrateModifier: subMod,
    masterBoostApplied,
    detailPremiumApplied,
  };
}

// ============================================================================
// COMPARISON METRICS
// ============================================================================

/**
 * Calculate Delta (variance between gut price and floor)
 * Δ = GP - TTCP
 * Negative = underpricing (profit leak)
 */
export function calculateDelta(gutPrice: number | null, ttcp: number): number | null {
  if (gutPrice === null || gutPrice <= 0) return null;
  return gutPrice - ttcp;
}

/**
 * Calculate Effective Hourly Rate (Gross)
 * EH = GP / H
 * User-facing metric — matches external benchmarks (ZipRecruiter, Artwork Archive).
 */
export function calculateEffectiveHourly(
  gutPrice: number | null,
  materials: number,
  hours: number
): number | null {
  if (gutPrice === null || gutPrice <= 0) return null;
  if (hours <= 0) return null;
  return gutPrice / hours;
}

/**
 * Calculate Net Effective Hourly Rate (after materials)
 * Net EH = (GP - M) / H
 * Used internally by forensic gates (e.g., minimum wage check).
 */
export function calculateNetEffectiveHourly(
  gutPrice: number | null,
  materials: number,
  hours: number
): number | null {
  if (gutPrice === null || gutPrice <= 0) return null;
  if (hours <= 0) return null;
  return (gutPrice - materials) / hours;
}

/**
 * Calculate Sustainability Ratio
 * S = EH / ST
 */
export function calculateSustainabilityRatio(
  effectiveHourly: number | null,
  survivalTarget: number
): number | null {
  if (effectiveHourly === null) return null;
  if (survivalTarget <= 0) return null;
  return effectiveHourly / survivalTarget;
}

// ============================================================================
// DENSITY CALCULATIONS
// ============================================================================

/**
 * Calculate density (hours per square inch)
 */
export function calculateDensity(hours: number, area: number): number {
  if (area <= 0) return 0;
  return hours / area;
}

/**
 * Get density thresholds with relaxer applied for paper mediums
 * Source: Addendum E Section 3.4
 */
export function getDensityThresholds(
  tier: TierKey,
  medium: MediumKey
): { densityMin: number; densityMax: number; relaxerApplied: boolean } {
  const tierConfig = TIERS[tier];
  const mediumConfig = MEDIUMS[medium];
  
  if (mediumConfig.isPaperBased) {
    return {
      densityMin: tierConfig.densityMin * DENSITY_RELAXER_FACTOR,
      densityMax: tierConfig.densityMax * DENSITY_RELAXER_FACTOR,
      relaxerApplied: true,
    };
  }
  
  return {
    densityMin: tierConfig.densityMin,
    densityMax: tierConfig.densityMax,
    relaxerApplied: false,
  };
}

// ============================================================================
// EXCEPTION CHECKS
// ============================================================================

/**
 * Check if mastery override applies
 * Source: Addendum E Section 3.1
 */
export function checkMasteryOverride(careerStage: CareerStageKey): boolean {
  return careerStage === 'established';
}

/**
 * Check if material exception applies
 * Source: Addendum E Section 3.2
 * Denominator: gutPrice (artist's actual price). Falls back to ttcp if unavailable.
 */
export function checkMaterialException(
  materials: number,
  ttcp: number,
  gutPrice?: number | null
): { exceeds: boolean; materialCheckBase: 'actual' | 'estimated' } {
  const base = (gutPrice != null && gutPrice > 0) ? gutPrice : ttcp;
  const materialCheckBase = (gutPrice != null && gutPrice > 0) ? 'actual' as const : 'estimated' as const;
  if (base <= 0) return { exceeds: false, materialCheckBase };
  return { exceeds: materials > (base * MATERIAL_EXCEPTION_THRESHOLD), materialCheckBase };
}

/**
 * Check if small work logic applies
 * Source: Addendum E Section 2.2
 */
export function checkSmallWorkLogic(area: number): boolean {
  return area < SIZE_THRESHOLDS.SMALL_WORK;
}

/**
 * Apply minimum floor protection for micro works
 * Source: Addendum E Section 1.2
 */
export function applyMinimumFloor(
  area: number,
  ttcp: number,
  currentPrice: number
): { price: number; applied: boolean } {
  if (area >= SIZE_THRESHOLDS.MINIMUM_FLOOR) {
    return { price: currentPrice, applied: false };
  }
  
  const minimumPrice = ttcp * MINIMUM_FLOOR_MULTIPLIER;
  if (currentPrice < minimumPrice) {
    return { price: minimumPrice, applied: true };
  }
  
  return { price: currentPrice, applied: false };
}

// ============================================================================
// COMPLETE VERDICT CALCULATION
// ============================================================================

/**
 * Generate a complete verdict from inputs
 * This is the main entry point for the calculation engine
 */
export function calculateVerdict(inputs: VerdictInputs): Omit<Verdict, 'id' | 'userId' | 'sold' | 'soldPrice' | 'soldDate' | 'createdAt' | 'updatedAt'> {
  const area = inputs.width * inputs.height;
  
  // Floor calculation
  const floor = calculateFloor(
    inputs.hours,
    inputs.monthlyBurn,
    inputs.monthlyCapacity,
    inputs.materials,
    inputs.tier
  );
  
  // Ceiling calculation
  const ceiling = calculateCeiling(
    inputs.medium,
    area,
    inputs.careerStage,
    inputs.tier,
    inputs.substrate
  );
  
  // Density
  const density = calculateDensity(inputs.hours, area);
  const densityThresholds = getDensityThresholds(inputs.tier, inputs.medium);
  
  // Comparison metrics
  const delta = calculateDelta(inputs.gutPrice, floor.ttcp);
  const effectiveHourly = calculateEffectiveHourly(
    inputs.gutPrice,
    inputs.materials,
    inputs.hours
  );
  const sustainabilityRatio = calculateSustainabilityRatio(
    effectiveHourly,
    floor.survivalTarget
  );
  
  // Exception checks
  const masteryOverride = checkMasteryOverride(inputs.careerStage);
  const materialResult = checkMaterialException(inputs.materials, floor.ttcp, inputs.gutPrice);
  const materialException = materialResult.exceeds;
  const smallWorkLogic = checkSmallWorkLogic(area);
  
  // Density validation
  const isUnderDensity = density < densityThresholds.densityMin;
  const isOverDensity = density > densityThresholds.densityMax;
  
  // Speed veto applies if under density AND no exceptions
  const speedVetoApplies = isUnderDensity && 
    !masteryOverride && 
    !materialException;
  
  // For small works, ignore over-density (allow infinite slowness)
  const effectiveOverDensity = smallWorkLogic ? false : isOverDensity;
  
  // Minimum floor protection
  const minFloorResult = applyMinimumFloor(area, floor.ttcp, floor.ttcp);
  
  // Build calculations object
  const calculations: VerdictCalculations = {
    area,
    survivalTarget: floor.survivalTarget,
    laborValue: floor.laborValue,
    baseCost: floor.baseCost,
    scopeTax: floor.scopeTax,
    ttcp: minFloorResult.price, // Apply minimum floor if needed
    psiBase: ceiling.psiBase,
    psiEffective: ceiling.psiEffective,
    sizeMultiplier: ceiling.sizeMultiplier,
    detailPremium: ceiling.detailPremium,
    ceiling: ceiling.ceiling,
    delta,
    effectiveHourly,
    sustainabilityRatio,
    density,
    densityMin: densityThresholds.densityMin,
    densityMax: densityThresholds.densityMax,
  };
  
  // Build flags object
  const flags: VerdictFlags = {
    isSpeedVeto: speedVetoApplies,
    suggestedTier: speedVetoApplies ? findTierByDensity(density) : null,
    isOverDensity: effectiveOverDensity,
    isCeilingBelowFloor: ceiling.ceiling < calculations.ttcp,
    masteryOverrideApplied: masteryOverride && isUnderDensity,
    materialExceptionApplied: materialException && isUnderDensity,
    materialCheckBase: materialResult.materialCheckBase,
    smallWorkLogicApplied: smallWorkLogic,
    minimumFloorApplied: minFloorResult.applied,
    detailPremiumApplied: ceiling.detailPremiumApplied,
    masterBoostApplied: ceiling.masterBoostApplied,
    densityRelaxerApplied: densityThresholds.relaxerApplied,
    calibrationOverride: false, // Set by validation layer
    sustainabilityStatus: getSustainabilityStatus(sustainabilityRatio),
  };
  
  return {
    inputs,
    calculations,
    flags,
    currency: inputs.currency,
  };
}
